/**
 * RichTextEditor - Editor de texto rico baseado em TipTap
 * Suporte a formatação, imagens inline e links
 * Com sanitização de segurança integrada
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-10 v1.1.0 - Botão de emoji na toolbar (reaproveita EmojiGrid
 *            do chat/fluxos). Nova prop opcional `showEmoji` (padrão true).
 *   RELEASE: v1.0.0 - Versão inicial.
 */
import React, { useCallback, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Link as LinkIcon, 
  Image as ImageIcon,
  Smile,
  Undo,
  Redo
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EmojiGrid } from '@/components/ui/emoji-grid';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize';


// Props do componente
interface RichTextEditorProps {
  /** Conteúdo HTML inicial */
  content?: string;
  /** Placeholder quando vazio */
  placeholder?: string;
  /** Callback ao alterar conteúdo */
  onChange?: (html: string) => void;
  /** Modo somente leitura */
  readOnly?: boolean;
  /** Altura mínima do editor */
  minHeight?: string;
  /** Altura máxima do editor */
  maxHeight?: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Mostrar toolbar de formatação */
  showToolbar?: boolean;
  /** Callback para inserir imagem */
  onInsertImage?: () => void;
  /** Tamanho da fonte: 'xs' | 'sm' | 'base' (padrão: 'sm') */
  fontSize?: 'xs' | 'sm' | 'base';
  /** Focar automaticamente ao montar */
  autoFocus?: boolean;
  /** Mostrar botão de emoji na toolbar (padrão: true) */
  showEmoji?: boolean;
}

/**
 * Botão de emoji da toolbar.
 * Componente próprio para isolar o estado do popover (evita hooks condicionais).
 */
function EmojiToolbarButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);

  // Insere o emoji na posição atual do cursor, mantendo o foco no editor
  const handleSelect = useCallback((emoji: string) => {
    editor.chain().focus().insertContent(emoji).run();
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          title="Inserir emoji"
          aria-label="Inserir emoji"
        >
          <Smile className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <EmojiGrid
          onSelect={handleSelect}
          height="320px"
          columns={8}
          showSearch
          showCategories
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Toolbar de formatação do editor
 */
function EditorToolbar({ 
  editor, 
  onInsertImage,
  showEmoji = true,
}: { 
  editor: Editor | null; 
  onInsertImage?: () => void;
  showEmoji?: boolean;
}) {
  if (!editor) return null;

  // Handler para adicionar link
  const addLink = useCallback(() => {
    const url = window.prompt('URL do link:');
    if (url) {
      editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
    }
  }, [editor]);


  return (
    <div className="flex items-center gap-0.5 p-1 border-b bg-muted/30 rounded-t-md flex-wrap">
      {/* Formatação de texto */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => (editor.chain().focus() as any).toggleBold().run()}
        className={cn(editor.isActive('bold') && 'bg-accent')}
        title="Negrito"
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => (editor.chain().focus() as any).toggleItalic().run()}
        className={cn(editor.isActive('italic') && 'bg-accent')}
        title="Itálico"
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>

      <Separator orientation="vertical" className="h-5 mx-1" />

      {/* Listas */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => (editor.chain().focus() as any).toggleBulletList().run()}
        className={cn(editor.isActive('bulletList') && 'bg-accent')}
        title="Lista com marcadores"
      >
        <List className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => (editor.chain().focus() as any).toggleOrderedList().run()}
        className={cn(editor.isActive('orderedList') && 'bg-accent')}
        title="Lista numerada"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </Button>

      <Separator orientation="vertical" className="h-5 mx-1" />

      {/* Link e Imagem */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={addLink}
        className={cn(editor.isActive('link') && 'bg-accent')}
        title="Adicionar link"
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </Button>
      {/* Emoji — disponível automaticamente em todas as telas que usam o editor */}
      {showEmoji && <EmojiToolbarButton editor={editor} />}

      {onInsertImage && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onInsertImage}
          title="Inserir imagem"
        >
          <ImageIcon className="h-3.5 w-3.5" />
        </Button>
      )}

      <Separator orientation="vertical" className="h-5 mx-1" />

      {/* Undo/Redo */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Desfazer"
      >
        <Undo className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Refazer"
      >
        <Redo className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/**
 * Handle exposto via ref para inserir conteúdo na posição do cursor.
 */
export interface RichTextEditorHandle {
  /** Insere texto puro na posição atual do cursor (ou substitui a seleção) */
  insertText: (text: string) => void;
  /** Foca o editor */
  focus: () => void;
}

/**
 * Componente principal do editor de texto rico
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor({
  content = '',
  placeholder = 'Digite sua descrição aqui...',
  onChange,
  readOnly = false,
  minHeight = '120px',
  maxHeight = '400px',
  className,
  showToolbar = true,
  onInsertImage,
  fontSize = 'sm',
  autoFocus = false,
  showEmoji = true,
}, ref) {

  // Classe de tamanho de fonte baseada na prop
  const fontSizeClass = {
    xs: 'prose-xs [&_.ProseMirror]:text-xs',
    sm: 'prose-sm [&_.ProseMirror]:text-sm',
    base: 'prose-base [&_.ProseMirror]:text-base',
  }[fontSize];
  // Configurar editor TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Desabilitar headings para manter simples
      }),
      Image.configure({
        inline: true,
        allowBase64: false, // Não permitir base64 por segurança
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-md my-2',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-2 hover:text-primary/80',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: sanitizeHtml(content), // Sanitizar conteúdo inicial
    editable: !readOnly,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      if (onChange) {
        // Sanitizar antes de enviar para o callback
        const html = editor.getHTML();
        onChange(sanitizeHtml(html));
      }
    },
  });

  // Atualizar conteúdo quando prop mudar externamente
  // Compara já sanitizado (evita loop quando a sanitização altera o HTML) e
  // não emite update (evita disparar onChange para uma mudança externa).
  useEffect(() => {
    if (!editor) return;
    const sanitized = sanitizeHtml(content);
    if (sanitized !== editor.getHTML()) {
      editor.commands.setContent(sanitized, { emitUpdate: false });
    }
  }, [content, editor]);


  // Atualizar estado de edição
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  // Handler para inserir imagem no editor
  const insertImage = useCallback((url: string) => {
    if (editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  // Expor API imperativa para inserir conteúdo na posição do cursor
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (!editor) return;
      // Foca preservando a última posição conhecida do cursor e insere
      editor.chain().focus().insertContent(text).run();
    },
    focus: () => {
      editor?.chain().focus().run();
    },
  }), [editor]);

  return (
    <div 
      className={cn(
        'rounded-md border bg-background',
        readOnly && 'border-transparent bg-transparent',
        className
      )}
    >
      {/* Toolbar de formatação (apenas no modo edição) */}
      {showToolbar && !readOnly && (
        <EditorToolbar editor={editor} onInsertImage={onInsertImage} showEmoji={showEmoji} />
      )}
      
      {/* Área do editor com tamanho de fonte configurável */}
      {/* Container clicável para focar o editor em qualquer área */}
      <div 
        className={cn(
          !readOnly && 'cursor-text'
        )}
        style={{ 
          minHeight: readOnly ? 'auto' : minHeight,
          maxHeight: maxHeight,
          overflowY: 'auto'
        }}
        onClick={() => {
          // Foca o editor ao clicar em qualquer área vazia
          if (editor && !readOnly) {
            editor.chain().focus().run();
          }
        }}
      >
        <EditorContent 
          editor={editor}
          className={cn(
            'prose max-w-none h-full',
            fontSizeClass,
            'dark:prose-invert',
            '[&_.ProseMirror]:outline-none',
            '[&_.ProseMirror]:p-3',
            '[&_.ProseMirror]:min-h-full',
            '[&_.ProseMirror.is-editor-empty]:before:content-[attr(data-placeholder)]',
            '[&_.ProseMirror.is-editor-empty]:before:text-muted-foreground',
            '[&_.ProseMirror.is-editor-empty]:before:float-left',
            '[&_.ProseMirror.is-editor-empty]:before:pointer-events-none',
            '[&_.ProseMirror.is-editor-empty]:before:h-0',
            readOnly && '[&_.ProseMirror]:p-0'
          )}
        />
      </div>
    </div>
  );
});

export type { RichTextEditorProps };
